<template>
  <div
    class="editor-audio-drag-upload"
    :class="{
      [`editor-audio-drag-upload--${createRenderType}`]: true,
    }"
  >
    <file-pond
      ref="refUpload"
      v-if="renderData.ifRenderFilePond"
      label-idle="歌曲文件"
      v-bind:allow-multiple="false"
      :acceptedFileTypes="renderData.acceptedFileTypes.join(',')"
      @handleAfterRead="handleAfterRead"
      @addfile="handleAddFile"
      v-bind:files="renderData.files"
    >
    </file-pond>

    <div
      class="editor-audio-drag-upload-main"
      ref="refUploadContent"
      @click.stop
    >
      <div class="editor-audio-drag-upload-icon flex-center">
        <i class="icon icon-add"> </i>
      </div>
      <div class="editor-audio-drag-upload-text tc">
        {{ props.uploadDescription }}
      </div>
      <div class="editor-audio-drag-upload-buttons">
        <div
          class="editor-audio-drag-upload-button"
          v-if="ModuleOpenStateControl.speControl.linkCrawling"
        >
          <Button
            @click="onAddLinkButtonClick"
            :renderStyle="{
              '--padding': '0',
              '--width': '100%',
              '--height': createRenderType == 'mobile' ? '36px' : '36px',
              '--borderRadius': '40px',
              '--background': 'rgba(148, 173, 255, 1)',
              '--fontColor': '#000',
              '--fontSize': '12px',
              '--fontWeight': 'normal',
              '--border': 'none',
              '--backgroundHover': 'rgba(148, 173, 255, 0.75)',
              '--fontColorHover': '#000',
            }"
          >
            <span class="fn-inline">{{
              $$t("editor.youtube_link_upload")
            }}</span>
          </Button>
        </div>
        <div class="editor-audio-drag-upload-button">
          <Button
            @click="onAddFileButtonClick"
            :renderStyle="{
              '--padding': '0',
              '--width': '100%',
              '--height': createRenderType == 'mobile' ? '36px' : '36px',
              '--borderRadius': '42px',
              '--background': 'rgba(148, 173, 255, 1)',
              '--fontColor': '#000',
              '--fontSize': '12px',
              '--fontWeight': 'normal',
              '--border': 'none',
              '--backgroundHover': 'rgba(148, 173, 255, 0.75)',
              '--fontColorHover': '#000',
            }"
          >
            <span class="fn-inline">{{ $$t("editor.file_upload") }}</span>
          </Button>
        </div>
      </div>
    </div>

    <AddLinkDialog
      v-model:visible="renderData.addLinkDialogVisible"
      @done="doAddReferenceLinkDone"
    />
  </div>
</template>

<script setup>
import {
  ref,
  defineProps,
  defineEmits,
  computed,
  readonly,
  reactive,
  watch,
  defineExpose,
  onMounted,
  onUpdated,
  onUnmounted,
  getCurrentInstance,
} from "vue";

import { useRouter, useRoute } from "vue-router";
import globalConfig, {
  chinaAppName,
  ModuleOpenStateControl,
} from "@/config/config.js";

import Button from "@/components/basic/button/Button.vue";
import AddLinkDialog from "./addLinkDialog.vue";

import { validateYoutubeLink } from "@/utils/validationTools.js";
import axios from "axios";
import apiCommon from "@/api/api/apiCommon.js";

import { $$language, $$t, $$tValue } from "@/i18n/i18n.js";
import tools from "@/utils/tools.js";
import ffmpegUtil from "@/components/media/utils/ffmpegUtil.js";
import message from "@/components/functionCallComponent/message.jsx";
import clipUtil from "@/components/media/utils/clipUtil.js";
import objectStorage, {
  getObjectStorageFullUrl,
} from "@/lib/objectStorage/objectStorage.js";

import { storeToRefs } from "pinia";
import useCreateLayoutStore from "@/store/create/createLayoutStore.js";
import useUserDataStore from "@/store/userDataStore.js";
const createLayoutStore = useCreateLayoutStore();
const userDataStore = useUserDataStore();
const { createRenderType } = storeToRefs(createLayoutStore);

import vueFilePond from "vue-filepond";
import "filepond/dist/filepond.min.css";

const globalProperties =
  getCurrentInstance()?.appContext.config.globalProperties;

const FilePond = vueFilePond();

const Router = useRouter();
const route = useRoute();

const refUpload = ref(null);
const refUploadContent = ref(null);
const props = defineProps({
  uploadDescription: {
    type: String,
    default: "",
  },
  pointTypePrefix: {
    type: String,
    default: "editor",
  },
});
const emits = defineEmits([
  "saveSuccess",
  "syncUploadState",
  "initEditData",
  "initEditClipFile",
]);

const acceptedFileTypes = [
  "audio/x-wav",
  "video/quicktime",
  "video/mp4",
  "audio/wav",
  "audio/x-m4a",
  "audio/flac",
  "audio/mpeg",
  ".mp3",
  ".wav",
  ".m4a",
  ".mp4",
  ".mov",
  ".MP4",
  ".flac",
];
const renderData = reactive({
  ifRenderFilePond: true,
  files: [],
  file: null,
  acceptedFileTypes: acceptedFileTypes,
  editFilePopupVisible: false,
  clipSourceData: null,
  editFileName: "",
  addLinkDialogVisible: false,
});

const checkMediaType = (file) => {
  if (file.type.toLocaleLowerCase().startsWith("audio")) {
    return acceptedFileTypes.includes(file.type.toLocaleLowerCase())
      ? "audio"
      : "unknown";
  } else if (file.type.toLocaleLowerCase().startsWith("video")) {
    return acceptedFileTypes.includes(file.type.toLocaleLowerCase())
      ? "video"
      : "unknown";
  } else {
    return "unknown";
  }
};

const initUploadContent = () => {
  if (refUploadContent.value) {
    nextTick(() => {
      refUpload.value?.$el
        .querySelector(".filepond--drop-label")
        .appendChild(refUploadContent.value);
    });
    setTimeout(() => {
      refUpload.value?.$el
        ?.querySelector("input")
        ?.setAttribute("accept", renderData.acceptedFileTypes.join(","));
    }, 20);
  }
};

const onAddLinkButtonClick = () => {
  renderData.addLinkDialogVisible = true;
  globalProperties.logCustomEvent(`${props.pointTypePrefix}_button_click`, {
    tab_stare: "youtube_link_upload",
    source: "youtube_link_upload",
  });
};

const onAddFileButtonClick = () => {
  refUpload.value?.$el.querySelector(".filepond--drop-label").dispatchEvent(
    new MouseEvent("click", {
      bubbles: true,
      cancelable: true,
      view: window,
    })
  );
  globalProperties.logCustomEvent(`${props.pointTypePrefix}_button_click`, {
    tab_stare: "file_upload",
    source: "file_upload",
  });
};

const handleAddFile = (error, fileItem, fileList) => {
  if (error) {
    console.error("File add error:", error);
    return;
  }
  console.log("File added:", fileItem);
  if (props.pointTypePrefix == "lyrics_translate") {
    handleAfterReadFile(fileItem.file, 2).then((data) => {
      emits("initEditClipFile", {
        file: data.file,
        duration: data.duration,
        title: data.title,
      });
    });
  } else {
    handleAfterRead(fileItem.file, 2).then((file) => {
      doUploadFile(file);
    });
  }
};
const handleAfterReadFile = (e) => {
  return new Promise(async (resolve, reject) => {
    let fileType = checkMediaType(e);
    if (fileType == "unknown") {
      message.info({
        position: "top",
        content: $$t("create.select_audio_or_video"),
      });
      reject("not audio");
      return;
    }
    let file = null;
    let blobUrl = "";
    if (e instanceof File) {
      file = e;
    } else {
      if (e.target.files && e.target.files.length > 0) {
        file = e.target.files[0];
      }
      renderData.ifRenderFilePond = false;
      renderData.files = [];
    }
    nextTick(() => {
      renderData.ifRenderFilePond = true;
      nextTick(() => {
        initUploadContent();
      });
    });
    if (!file) return;
    if (file.size > 1024 * 1024 * 100) {
      message.info({
        position: "top",
        content: $$t("create.max_file_size"),
      });
      reject("max file size");
      return;
    }
    try {
      const duration = await tools.getMediaFileDuration(file);
      if (duration > 0) {
        if (duration < 10) {
          message.info({
            position: "top",
            content: $$tValue("create.min_file_duration", { duration: 10 }),
          });
          reject("min file duration");
        } else {
          resolve({
            file: file,
            duration: duration,
            title: file.name?.replace(/.[a-zA-Z0-9]+$/, "").slice(0, 50),
          });
        }
      } else {
        console.log("getMediaFileDuration", duration, file?.name, file?.type);
        message.info({
          position: "top",
          content: $$t("create.select_audio_or_video"),
        });
        reject("not audio");
        return;
      }
    } catch (e) {
      const closeMsg = message.loading({
        content: $$t("create.extract_song"),
      });
      try {
        const outputfile = await ffmpegUtil.transFileToMp3(file);
        let duration = 0;
        if (outputfile) {
          duration = await tools.getMediaFileDuration(outputfile.file);
        }
        closeMsg.clear();
        if (duration > 0) {
          if (duration < 10) {
            message.info({
              position: "top",
              content: $$tValue("create.min_file_duration", { duration: 10 }),
            });
            reject("min file duration");
          } else {
            resolve({
              file: outputfile.file,
              duration: duration,
              title: file.name?.replace(/.[a-zA-Z0-9]+$/, "").slice(0, 50),
            });
          }
        } else {
          message.info({
            position: "top",
            content: $$t("create.select_audio_or_video"),
          });
          reject("not audio");
          return;
        }
      } catch (e) {
        closeMsg.clear();
        console.log("not audio", e, file?.name, file?.type);
        message.info({
          position: "top",
          content:
            fileType == "video"
              ? $$t("create.select_video_error")
              : $$t("create.select_audio_error"),
        });
        reject("not audio");
      }
    }
  });
};

const handleAfterRead = (e) => {
  return new Promise(async (resolve, reject) => {
    let fileType = checkMediaType(e);
    if (fileType == "unknown") {
      message.info({
        position: "top",
        content: $$t("create.select_audio_or_video"),
      });
      reject("not audio");
      return;
    }
    console.log("File handleAfterRead:", e);
    let file = null;
    if (e instanceof File) {
      file = e;
    } else {
      if (e.target.files && e.target.files.length > 0) {
        file = e.target.files[0];
      }
      renderData.ifRenderFilePond = false;
      renderData.files = [];
    }
    nextTick(() => {
      renderData.ifRenderFilePond = true;
      nextTick(() => {
        initUploadContent();
      });
    });
    if (!file) return;
    if (file.size > 1024 * 1024 * 100) {
      message.info({
        position: "top",
        content: $$t("create.max_file_size"),
      });
      reject("max file size");
      return;
    }

    const closeMsg = message.loading({
      position: "center",
      content: $$t("editor.audio_edit_data_processing"),
    });
    const minDuration = ["region_editing", "trim_editing"].includes(
      props.pointTypePrefix
    )
      ? 30
      : 15;
    const maxDuration = [
      "region_editing",
      "cover_voice",
      "trim_editing",
    ].includes(props.pointTypePrefix)
      ? 4 * 60 + 30
      : 7 * 60;
    const maxDurationTip = [
      "region_editing",
      "cover_voice",
      "trim_editing",
    ].includes(props.pointTypePrefix)
      ? $$tValue("editor.max_duration")
      : $$tValue("editor.max_duration_mins", { minutes: 7 });

    try {
      const duration = await tools.getMediaFileDuration(file);
      closeMsg.clear();
      if (duration > 0) {
        if (duration < minDuration) {
          message.info({
            position: "top",
            content: $$tValue("editor.min_duration", { duration: minDuration }),
          });
          reject("min file duration");
        } else if (duration > maxDuration) {
          message.info({
            position: "top",
            content: maxDurationTip,
          });
          reject("max file duration");
        } else {
          renderData.duration = duration;
          renderData.file = file;
          resolve(file);
        }
      } else {
        closeMsg.clear();
        console.log("getMediaFileDuration", duration, file?.name, file?.type);
        message.info({
          position: "top",
          content: $$t("create.select_audio_or_video"),
        });
        reject("not audio");
        return;
      }
    } catch (e) {
      try {
        const outputfile = await ffmpegUtil.transFileToMp3(file);
        let duration = 0;
        if (outputfile) {
          duration = await tools.getMediaFileDuration(outputfile.file);
        }
        closeMsg.clear();
        if (duration > 0) {
          if (duration < minDuration) {
            message.info({
              position: "top",
              content: $$tValue("editor.min_duration", {
                duration: minDuration,
              }),
            });
            reject("min file duration");
          } else if (duration > maxDuration) {
            message.info({
              position: "top",
              content: maxDurationTip,
            });
            reject("max file duration");
          } else {
            renderData.duration = duration;
            renderData.file = outputfile.file;
            resolve(renderData.file);
          }
        } else {
          console.log(
            "transFileToMp3 getMediaFileDuration",
            duration,
            file?.name,
            file?.type
          );
          message.info({
            position: "top",
            content: $$t("create.select_audio_or_video"),
          });
          reject("not audio");
          return;
        }
      } catch (e) {
        closeMsg.clear();
        console.log("not audio", e, file?.name, file?.type);
        message.info({
          position: "top",
          content:
            fileType == "video"
              ? $$t("create.select_video_error")
              : $$t("create.select_audio_error"),
        });
        reject("not audio");
      }
    }
  });
};

const doDownloadFile = async (url, filename) => {
  return new Promise((resolve, reject) => {
    var xhr = new XMLHttpRequest();
    xhr.open("GET", url, true);
    xhr.responseType = "blob";
    xhr.onload = function (e) {
      if (this.status === 200) {
        var blob = new Blob([this.response], {
          type: this.response?.type || "text/plain",
        });
        const resfile = new File([blob], filename, { type: blob.type });
        resolve(resfile);
      } else {
        reject(this.statusText);
      }
    };
    xhr.send();
  });
};

const doSaveLink = async () => {
  return new Promise((resolve, reject) => {
    if (
      !validateYoutubeLink(renderData.linkUrl)
    ) {
      message.error({
        position: "top",
        content: $$t("create.use_youtube"),
      });
      reject("invalid youtube link");
      return;
    }
    const params = {
      url: renderData.linkUrl,
    };
    renderData.isLoading = true;
    const closeMsg = message.loading({ content: $$t("create.parsing_link") });
    const minDuration = [
      "region_editing",
      "trim_editing",
      "cover_voice",
    ].includes(props.pointTypePrefix)
      ? 30
      : 15;
    const maxDuration = [
      "region_editing",
      "cover_voice",
      "trim_editing",
    ].includes(props.pointTypePrefix)
      ? 4 * 60 + 30
      : 7 * 60;
    const maxDurationTip = [
      "region_editing",
      "cover_voice",
      "trim_editing",
    ].includes(props.pointTypePrefix)
      ? $$tValue("editor.max_duration")
      : $$tValue("editor.max_duration_mins", { minutes: 7 });
    apiCommon
      .saveReferenceMusicTmp({
        url: renderData.linkUrl,
        min_duration_milliseconds: minDuration * 1000,
        max_duration_milliseconds: maxDuration * 1000,
      })
      .then((res) => {
        closeMsg?.clear();
        if (res.code == 200) {
          if (res.data?.duration < minDuration * 1000) {
            message.info({
              position: "top",
              content: $$tValue("editor.min_duration", {
                duration: minDuration,
              }),
            });
            reject("min file duration");
          } else if (res.data?.duration > maxDuration * 1000) {
            message.info({
              position: "top",
              content: maxDurationTip,
            });
            reject("max file duration");
          } else {
            resolve(res.data);
          }
        } else {
          if (res.code == 6126) {
            message.info({
              position: "top",
              content: $$tValue("editor.min_duration", {
                duration: minDuration,
              }),
            });
            reject("min file duration");
          } else if (res.code == 6127) {
            message.info({
              position: "top",
              content: maxDurationTip,
            });
            reject("max file duration");
          } else if (
            res.code == 6116 ||
            (res.data?.duration_milliseconds ?? res.data?.duration ?? 0) ==
              7 * 1000 * 60
          ) {
            renderData.linkUrl = "";
            message.info({
              position: "top",
              content: maxDurationTip,
            });
          } else if (res.code == 1112) {
            message.error({
              position: "top",
              content: $$t("create.cur_position"),
            });
          } else {
            message.error({
              position: "top",
              content: $$t("editor.parse_yoputube_link_failed"),
            });
          }
          reject("invalid spider link");
        }
      })
      .catch((e) => {
        closeMsg?.clear();
        message.error({
          position: "top",
          content: $$t("editor.parse_yoputube_link_failed"),
        });
        reject(e);
      });
  });
};

const doSaveLinkFile = async () => {
  return new Promise((resolve, reject) => {
    if (
      !validateYoutubeLink(renderData.linkUrl)
    ) {
      message.error({
        position: "top",
        content: $$t("create.use_youtube"),
      });
      reject("invalid youtube link");
      return;
    }
    const params = {
      url: renderData.linkUrl,
    };
    renderData.isLoading = true;
    const closeMsg = message.loading({ content: $$t("create.parsing_link") });
    apiCommon
      .saveReferenceMusicTmp({
        url: renderData.linkUrl,
      })
      .then((res) => {
        if (res.code == 200) {
          renderData.editFileName = res.data?.name ?? "";
          doDownloadFile(
            getObjectStorageFullUrl(res.data?.cos_url),
            (res.data?.name ?? "audio_from_youtue").replace(/\s+/, "_") +
              "." +
              tools.getFileExtension(res.data?.cos_url ?? ".mp3")
          )
            .then((linkFile) => {
              closeMsg?.clear();
              resolve({
                file: linkFile,
                duration: res.data?.duration / 1000,
                linkParseUrl: getObjectStorageFullUrl(res.data?.cos_url),
                title: res.data?.name,
              });
            })
            .catch((e) => {
              closeMsg?.clear();
              reject(e);
            });
        } else {
          closeMsg?.clear();
          if (
            res.code == 6116 ||
            (res.data?.duration_milliseconds ?? res.data?.duration ?? 0) ==
              7 * 1000 * 60
          ) {
            renderData.linkUrl = "";
            message.error({
              position: "top",
              content: $$t("create.style_link_max_duration"),
            });
          } else if (res.code == 1112) {
            message.error({
              position: "top",
              content: $$t("create.cur_position"),
            });
          } else {
            message.error({
              position: "top",
              content: $$t("create.invalid_link"),
            });
          }
          reject("invalid spider link");
        }
      })
      .catch((e) => {
        closeMsg?.clear();
        message.error({
          position: "top",
          content: $$t("create.invalid_link"),
        });
        reject(e);
      });
  });
};

const doAddReferenceLinkDone = (url) => {
  renderData.linkUrl = url;
  if (props.pointTypePrefix == "lyrics_translate") {
    doSaveLinkFile()
      .then((data) => {
        emits("initEditClipFile", {
          file: data.file,
          duration: data.duration,
          linkParseUrl: data.linkParseUrl,
          title: data.title,
          resource: renderData.linkUrl,
          isLink: true,
        });
      })
      .catch((e) => {
        console.error("doSaveLink error", e);
      });
  } else {
    doSaveLink()
      .then((data) => {
        doInitEditData(
          {
            linkUrl: url,
            source_type: 1,
            title: data.name?.slice(0, 50),
            cos_url: data.cos_url,
            duration_milliseconds: data.duration,
          },
          true
        );
      })
      .catch((e) => {
        console.error("doSaveLink error", e);
      });
  }
};

const doUploadFile = async (file) => {
  const closeMsg = message.loading({
    position: "center",
    content: $$t("editor.audio_edit_data_processing"),
  });
  if (
    !(
      clipUtil.isMP3ByMimeType(file.type) ||
      clipUtil.isMP3ByExtension(file.name)
    )
  ) {
    try {
      const outputfile = await ffmpegUtil.transFileToMp3(file);
      file = outputfile.file;
    } catch (e) {
      console.error("ffmpegUtil.transFileToMp3 error", e);
    }
  }
  const objectStorageRes = await objectStorage.uploadFileToObjectStorage(
    file,
    (obj) => {
      renderData.uploadCallbackData = obj;
    }
  );
  const saveData = {
    source_type: 2,
    title: renderData.file.name?.replace(/.[a-zA-Z0-9]+$/, "").slice(0, 50),
    cos_url: objectStorageRes.pathKey,
    duration_milliseconds: Math.round(renderData.duration * 1000),
  };
  closeMsg.clear();
  doInitEditData(saveData);
};

const doInitEditData = (data, isLink = false) => {
  emits("initEditData", data);
  renderData.addLinkDialogVisible = false;
};

const syncUploadState = (state) => {
  emits("syncUploadState", state);
};

onMounted(() => {
  nextTick(() => {
    initUploadContent();
  });
});
</script>
<style lang="scss">
.editor-audio-drag-upload {
  --filepond-border-radius: 12px 12px 0 0;
  --filepond-min-height: 196px;
  .editor-audio-drag-upload-main {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    padding: 22px 0 23px 0;
    border-radius: 12px 12px 0 0;
  }
  .editor-audio-drag-upload-icon {
    width: 64px;
    height: 64px;
    min-width: 64px;
    min-height: 64px;
    border-radius: 50%;
    background: rgba(148, 173, 255, 0.3);
    i {
      height: 25px;
      width: 35px;
      background: url(@/assets/img/editor/upload-drag-ico.png);
      background-size: 100% 100%;
    }
  }
  .editor-audio-drag-upload-text {
    font-family: HarmonyOS Sans SC;
    font-size: 12px;
    color: #ffffff;
    margin: 12px 12px;
    line-height: 20px;
  }
  .filepond--root {
    height: max-content !important;
    min-height: var(--filepond-min-height) !important;
    background: transparent;
    box-shadow: none;
    align-items: center;
    justify-content: center;
    border-radius: var(--filepond-border-radius);
    margin: 0;
    contain: unset !important;
    line-height: 0;
    .filepond--wrapper {
      height: max-content !important;
      border-radius: var(--filepond-border-radius);
    }
    .filepond--browser.filepond--browser {
      display: none;
    }
    .filepond--list-scroller {
      display: none;
    }
    .filepond--credits {
      display: none;
    }

    .filepond--panel-root {
      border-radius: 0.5em;
      background: transparent;
      box-shadow: none;
      border-radius: max-content;
      display: none;
    }

    .filepond--assistant {
      display: none;
    }
    .filepond--drop-label {
      display: block;
      position: unset !important;
      visibility: visible !important;
      transform: translate3d(0px, 0, 0px) !important;
      height: max-content !important;
      border-radius: var(--filepond-border-radius) !important;
      min-height: var(--filepond-min-height) !important;
      opacity: 1 !important;
      pointer-events: auto !important;
      & > label {
        display: none;
      }
    }
    .filepond--data {
      display: none;
    }

    &[data-hopper-state="drag-over"] {
      .editor-audio-drag-upload-main {
        border: 1px dashed #94adff;
      }
    }
  }
  .editor-audio-drag-upload-buttons {
    display: flex;
    align-items: center;

    .editor-audio-drag-upload-button {
      width: 180px;
      &:not(:last-child) {
        margin-right: 12px;
      }
    }
  }

  &.editor-audio-drag-upload--mobile {
    --filepond-height: 268px;
    .editor-audio-drag-upload-main {
      padding: 32px 0 20px 0;
    }
    .editor-audio-drag-upload-text {
      line-height: 16px;
      margin: 12px 30px;
    }
    .editor-audio-drag-upload-icon {
      width: 64px;
      height: 64px;
      min-width: 64px;
      min-height: 64px;
      border-radius: 50%;
      background: rgba(148, 173, 255, 0.3);
      i {
        height: 25px;
        width: 35px;
        background: url(@/assets/img/editor/upload-drag-ico.png);
        background-size: 100% 100%;
      }
    }
    .editor-audio-drag-upload-buttons {
      padding: 0 16px;
      width: 100%;
      flex-direction: column;
      .editor-audio-drag-upload-button {
        width: 100%;
        margin: 0 0 12px 0;
        &:last-child {
          margin: 0;
        }
      }
    }
  }
}
</style>
